import React, { useState, useCallback, useEffect, useContext, useRef } from "react";
import truncate from "truncate-html";

import { DEFAULT_READ_LESS_TEXT, DEFAULT_READ_MORE_TEXT } from "src/constants/wall.constant";

export const ReadMoreTextContext = React.createContext();
const MAX_SCROLL_TARGET_ANCESTOR_STEPS = 1000;
const SCROLL_STABILIZATION_DELAY_FRAMES = 3;
const SCROLL_STABLE_WINDOW_MS = 180;
const SCROLL_STABILIZATION_MAX_MS = 1200;

const ReadMoreMaster = ( {
    byWords,
    elementId,
    parentClass,
    readLess,
    options,
    readMore,
    length,
    refresh,
    ellipsis,
    spaceBefore,
    text,
    parse,
  } ) => {

  const readMoreTextContext = useContext(ReadMoreTextContext);
  const containerRef = useRef(null);
  const pendingScrollSourceRef = useRef(null);
  const scrollAnimationFrameRef = useRef([]);
  const scrollTimeoutRef = useRef(null);
  const maxScrollTimeoutRef = useRef(null);
  const resizeObserverRef = useRef(null);

  const [showMore, setShowMore] = useState(false);

  truncate.setup({
    byWords: !!byWords,
    ellipsis: ellipsis ? ellipsis : "...."
  });

  const [state, setState] = useState({
    showRealData: false,
    realData: (text && text!=='')? text.replace(/<br\s*\/?>/gi,'<br>'): text,
    truncatedData: truncate(text, length ? length : 2)
  });

  const clearScheduledScrolls = useCallback(() => {
    scrollAnimationFrameRef.current.forEach((frameId) => window.cancelAnimationFrame(frameId));
    scrollAnimationFrameRef.current = [];

    if(scrollTimeoutRef.current){
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }

    if(maxScrollTimeoutRef.current){
      window.clearTimeout(maxScrollTimeoutRef.current);
      maxScrollTimeoutRef.current = null;
    }

    if(resizeObserverRef.current){
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
  }, []);

  const resolveScrollTarget = useCallback((sourceNode) => {
    try{
      let currentNode = sourceNode || containerRef.current;
      let steps = 0;
      const visitedNodes = new Set();

      while (currentNode && steps < MAX_SCROLL_TARGET_ANCESTOR_STEPS && !visitedNodes.has(currentNode)) {
        visitedNodes.add(currentNode);

        if (!elementId || currentNode.id === elementId) {
          return currentNode;
        }

        currentNode = currentNode.parentElement;
        steps += 1;
      }

      if(elementId){
        // Look the id up in the tree this component actually lives in:
        // inside the no-iframe embed that's a ShadowRoot, where
        // document.getElementById can't see. getRootNode() returns the
        // ShadowRoot there and the document on the iframe pages.
        const startNode = sourceNode || containerRef.current;
        const rootNode = startNode && typeof startNode.getRootNode === "function"
          ? startNode.getRootNode()
          : document;

        if(rootNode && typeof rootNode.getElementById === "function"){
          return rootNode.getElementById(elementId);
        }

        return document.getElementById(elementId);
      }
    }
    catch(err){}

    return null;
  }, [elementId])

  const requestEmbedResize = useCallback(() => {
    try{
      if(refresh){
        refresh();
      }

      if(
        window.top !== window.self &&
        window.parentIFrame &&
        typeof window.parentIFrame.size === "function"
      ){
        window.parentIFrame.size();
      }
    }
    catch(err){}
  }, [refresh])

  const scrollElement = useCallback((sourceNode) =>{
    try{
      const elemScroll = resolveScrollTarget(sourceNode);

      if(elemScroll){
        const elementRect = elemScroll.getBoundingClientRect();
        const scrollTop = elementRect.top + (window.pageYOffset || window.scrollY || 0);
        const scrollLeft = elementRect.left + (window.pageXOffset || window.scrollX || 0);

        if (
          window.top !== window.self &&
          window.parentIFrame &&
          typeof window.parentIFrame.scrollToOffset === "function"
        ) {
          window.parentIFrame.scrollToOffset(scrollLeft, scrollTop);
        }
        else if(typeof window.scrollTo === "function"){
          window.scrollTo({
            top: scrollTop,
            left: scrollLeft,
            behavior: 'smooth'
          });
        }
        else{
          elemScroll.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
      }
    }
    catch(err){}
  }, [resolveScrollTarget])

  useEffect(()=>{
    setState({
        ...state,
        truncatedData: truncate(text, length? length : 2)
    })
    if(refresh){
      refresh()
    }
  },[length])

  const handleShowData = useCallback((event) => {
    const nextShowRealData = !state.showRealData;
    const sourceNode = event?.currentTarget || containerRef.current;

    pendingScrollSourceRef.current = sourceNode;

    setState((prevState) => ({
      ...prevState,
      showRealData: nextShowRealData
    }));
    if (refresh) {
      refresh();
    }
  }, [refresh, state.showRealData]);

  const startScrollStabilization = useCallback((sourceNode) => {
    const observedNodes = [resolveScrollTarget(sourceNode), containerRef.current].filter((node, index, arr) => (
      node && arr.indexOf(node) === index
    ));

    const runScrollCorrection = () => {
      requestEmbedResize();
      scrollElement(sourceNode);
    };

    const finishScrollStabilization = () => {
      runScrollCorrection();
      clearScheduledScrolls();
      pendingScrollSourceRef.current = null;
    };

    const scheduleStableStop = () => {
      if(scrollTimeoutRef.current){
        window.clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = window.setTimeout(() => {
        finishScrollStabilization();
      }, SCROLL_STABLE_WINDOW_MS);
    };

    runScrollCorrection();
    scheduleStableStop();

    if(typeof ResizeObserver !== "undefined" && observedNodes.length>0){
      resizeObserverRef.current = new ResizeObserver(() => {
        runScrollCorrection();
        scheduleStableStop();
      });

      observedNodes.forEach((node) => {
        resizeObserverRef.current.observe(node);
      });
    }

    maxScrollTimeoutRef.current = window.setTimeout(() => {
      finishScrollStabilization();
    }, SCROLL_STABILIZATION_MAX_MS);
  }, [clearScheduledScrolls, requestEmbedResize, resolveScrollTarget, scrollElement]);

  useEffect(() => {
    const sourceNode = pendingScrollSourceRef.current;

    if(!sourceNode){
      return undefined;
    }

    const frameIds = [];
    const queueScroll = (remainingFrames = SCROLL_STABILIZATION_DELAY_FRAMES) => {
      const frameId = window.requestAnimationFrame(() => {
        if (remainingFrames > 1) {
          queueScroll(remainingFrames - 1);
          return;
        }

        startScrollStabilization(sourceNode);
      });

      frameIds.push(frameId);
    };

    clearScheduledScrolls();
    queueScroll();
    scrollAnimationFrameRef.current = frameIds;

    return () => {
      frameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
    };
  }, [clearScheduledScrolls, startScrollStabilization, state.showRealData]);

  useEffect(() => {
    return () => {
      clearScheduledScrolls();
    };
  }, [clearScheduledScrolls]);

  useEffect(()=>{
    if(state && state.realData && state.truncatedData && state.realData.length>length && state.realData.length>state.truncatedData.length){
            setShowMore(true)

    }

  },[state])
  
  useEffect(() => {
    if (byWords && text) {
      const textDetails = text.replace(/<[^>]+>/g, "");
      const arr = textDetails.split(" ");
      if (arr.length > length) {
        setShowMore(true);
      }
    }
  }, [byWords]);


  // Calculate max-height based on line-height (approximately 6-7 lines)
  // Assuming line-height of 1.5-1.7 and font-size of 1.15rem (18.4px) or 1.0rem (16px)
  // For consistency, use approximately 6 lines: 6 * 1.6 * 18px ≈ 173px, rounded to 180px
  const collapsedMaxHeight = '180px';
  const getLabelText = (contextText, propText, fallbackText) => {
    if (typeof contextText === 'string' && contextText.trim() !== '') {
      return contextText;
    }
    if (typeof propText === 'string' && propText.trim() !== '') {
      return propText;
    }
    return fallbackText;
  };

  const resolvedReadMoreText = getLabelText(
    readMoreTextContext?.readMoreText,
    readMore,
    DEFAULT_READ_MORE_TEXT
  );
  const resolvedReadLessText = getLabelText(
    readMoreTextContext?.readLessText,
    readLess,
    DEFAULT_READ_LESS_TEXT
  );

  return (
    <div 
      ref={containerRef}
      className="read-more-container" 
      style={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: showMore && !state.showRealData ? collapsedMaxHeight : 'auto'
      }}
    >
      <div 
        className="read-more-content"
        style={{
          overflow: 'hidden',
          maxHeight: showMore && !state.showRealData ? collapsedMaxHeight : 'none',
          transition: 'max-height 0.3s ease-out',
          position: 'relative'
        }}
      >
        {options?
          parse(`${
            !state.showRealData ? state.truncatedData : state.realData
          }`,options)
          :
          parse(`${
            !state.showRealData ? state.truncatedData : state.realData
          }`)
        }
      </div>

      {spaceBefore === false ? "" : " "}

      {showMore && (
        <div style={{ marginTop: '8px', flexShrink: 0 }}>
          <div className="font-roboto cursor-pointer underline" style={{fontSize:15}} onClick={handleShowData}>
            {!state.showRealData ? resolvedReadMoreText : resolvedReadLessText}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadMoreMaster;
